/**
 * Following a job on the server until it ends: the asking that every kind of job shares.
 */

import type { TheoryJob } from "@app/shared-types";
import { ApiError } from "../api/client";

/** How often a running job is asked how far it has got. */
const POLL_MS = 750;

/**
 * How many askings in a row may fail to reach the server before the job is given up on. A
 * page back from the background, a phone unlocked, may find the asking it was in the middle
 * of lost and the network back only a moment later; a server that is down stays unreached.
 */
const UNREACHED = 20;

/** What a failure said, as words for the page. */
export function said(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/**
 * Ask after a job until it is no longer running, telling `told` each progress it reports.
 *
 * Resolves with what the job made; with `null` when it was cancelled, or when `live` says
 * the page has stopped waiting for it. Rejects with the job's own words when it failed, with
 * the server's when it answered with a refusal, and with the network's when {@link UNREACHED}
 * askings in a row did not reach it.
 */
export async function followed<Progress, Result>(
  ask: () => Promise<TheoryJob<Progress, Result>>,
  live: () => boolean,
  told: (progress: Progress) => void,
  unreached = 0,
): Promise<Result | null> {
  const later = async (missed: number): Promise<Result | null> => {
    await new Promise<void>((resolve) => window.setTimeout(resolve, POLL_MS));
    return live() ? followed(ask, live, told, missed) : null;
  };
  let found: TheoryJob<Progress, Result>;
  try {
    found = await ask();
  } catch (cause) {
    // An answer from the server is its word on the job; only an asking that never reached it
    // is asked again.
    if (cause instanceof ApiError || unreached + 1 >= UNREACHED) throw cause;
    return live() ? later(unreached + 1) : null;
  }
  if (!live()) return null;
  if (found.state === "running") {
    told(found.progress);
    return later(0);
  }
  if (found.state === "failed") throw new Error(found.error?.message ?? "the run failed");
  return found.result ?? null;
}
